import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp';
import * as React from 'react';

import { InputLabel } from '@/components/primitives/Input';
import { cn } from '@/lib/utils';

import type { SlotProps } from 'input-otp';

// Brand is a CSS variable, so `ring-brand/20` can't apply the alpha and falls back to the default ring colour.
const BRAND_RING = 'ring-[color-mix(in_srgb,var(--color-brand)_16%,transparent)]';

export interface OneTimeCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired when the code becomes complete, so the form can submit itself. */
  onComplete?: (value: string) => void;
  length?: number;
  label?: string;
  /** Keep the label for screen readers only. */
  hideLabel?: boolean;
  error?: string;
  disabled?: boolean;
  /** Put the caret in the first box on mount, for a screen whose only job is this code. */
  focusOnMount?: boolean;
  /** `grouped`: two groups of large, leaf-cornered boxes, smaller below `xl`. */
  variant?: 'default' | 'grouped';
  className?: string;
}

export function OneTimeCodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  label,
  hideLabel = false,
  error,
  disabled = false,
  focusOnMount = false,
  variant = 'default',
  className,
}: OneTimeCodeInputProps) {
  const inputId = React.useId();
  const errorId = `${inputId}-error`;

  return (
    <div className={cn('flex flex-col', className)}>
      {label && (
        <InputLabel htmlFor={inputId} className={cn(hideLabel && 'sr-only')}>
          {label}
        </InputLabel>
      )}
      <OTPInput
        id={inputId}
        value={value}
        onChange={onChange}
        onComplete={onComplete}
        maxLength={length}
        pattern={REGEXP_ONLY_DIGITS}
        pasteTransformer={(pasted) => pasted.replace(/\D/g, '')}
        inputMode="numeric"
        autoComplete="one-time-code"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={focusOnMount}
        disabled={disabled}
        dir="ltr"
        aria-label={label ? undefined : 'Verification code'}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        containerClassName={cn(disabled && 'opacity-50')}
        render={({ slots }) =>
          variant === 'grouped' ? (
            <GroupedSlots slots={slots} error={!!error} />
          ) : (
            <div dir="ltr" aria-hidden="true" className="flex w-full gap-xs">
              {slots.map(({ char, isActive, hasFakeCaret }, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key -- Slots have fixed positional identities.
                  key={index}
                  className={cn(
                    'relative flex h-12 w-full min-w-0 items-center justify-center rounded-[6px] border border-solid bg-surface',
                    'text-lg font-semibold tabular-nums transition-colors',
                    error ? 'border-status-danger' : 'border-outline-input',
                    char && !error && 'border-foreground-muted',
                    isActive && cn('border-brand ring-2', BRAND_RING)
                  )}
                >
                  {char}
                  {hasFakeCaret && <span className="caret-blink absolute h-5 w-px bg-foreground" />}
                </div>
              ))}
            </div>
          )
        }
      />
      {error && (
        <p id={errorId} role="alert" className="pt-xs text-sm text-status-danger">
          {error}
        </p>
      )}
    </div>
  );
}

OneTimeCodeInput.displayName = 'OneTimeCodeInput';

function GroupedSlots({ slots, error }: { slots: SlotProps[]; error: boolean }) {
  const half = Math.ceil(slots.length / 2);
  const groups = [slots.slice(0, half), slots.slice(half)];

  return (
    // Below 400px wide the gaps tighten and the boxes shrink, so all six fit a small phone.
    <div
      dir="ltr"
      aria-hidden="true"
      className="flex w-full items-center justify-center gap-lg min-[400px]:gap-xxxl"
    >
      {groups.map((group, groupIndex) => (
        <div
          // eslint-disable-next-line react/no-array-index-key -- The two groups never reorder.
          key={groupIndex}
          className="flex min-w-0 items-center gap-xs min-[400px]:gap-sm xl:gap-md"
        >
          {group.map(({ char, isActive, hasFakeCaret }, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key -- Slots have fixed positional identities.
              key={index}
              className={cn(
                'relative flex h-14 w-11 min-w-0 shrink items-center justify-center rounded-bl-[11px] rounded-tr-[11px] border border-solid bg-surface',
                'xl:h-[88px] xl:w-[72px] xl:shrink-0 xl:rounded-bl-[18px] xl:rounded-tr-[18px]',
                'font-title text-size-2 font-semibold tabular-nums text-foreground transition-colors xl:text-size-4',
                error ? 'border-status-danger' : 'border-outline-input',
                isActive &&
                  'border-[1.5px] border-brand shadow-[0_0_16px_3px_rgba(0,0,0,0.08)] xl:shadow-[0_0_16px_4px_rgba(0,0,0,0.08)]'
              )}
            >
              {char}
              {hasFakeCaret && (
                <span className="caret-blink h-5 w-0.5 rounded-[1px] bg-brand xl:h-8" />
              )}
              {!char && !isActive && <span className="h-px w-2.5 bg-outline-input xl:w-3.5" />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
